import { Add, CalculateOutlined, Remove } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import COLORS from '../../colors';
import { MONTH_PASCAL } from '../../constants';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { SelectField } from '../SelectField';
import { ContentContainer } from '../containers';
import { getMonthsInRange } from '../../services/date';
import { format, parseISO } from 'date-fns';
import { useAppSelector } from '../../redux/hooks';
import { mapPascalMonthToLowercaseMonth, roundTwoDecimals } from '../../util';

interface CampaignCalculatorProps {
  startDate: string;
  endDate: string;
}

const StyledHeadingContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledHeading = styled.p`
  color: ${COLORS.NEUTRAL_200};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const StyledTwoColumn = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

const StyledRightColumn = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
`;

const StyledPillLabel = styled.p`
  color: ${COLORS.NAV_ACTIVE};
  font-size: 13.33px;
  font-weight: 600;
  line-height: 16px;
`;

const StyledPillValue = styled.p`
  color: ${COLORS.NAV_ACTIVE};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const StyledBluePill = styled.div`
  display: flex;
  padding: 8px 12px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  background-color: ${COLORS.CHECKED};
`;

const StyledGreyPill = styled.div`
  display: flex;
  padding: 8px 12px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  background-color: ${COLORS.PILL};
  width: 100%;
`;

const StyledExtraSection = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  box-sizing: border-box;
  gap: 12px;
  text-align: left;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const CampaignCalculator = ({
  startDate,
  endDate
}: CampaignCalculatorProps) => {
  const { calculated, detail } = useAppSelector((state) => state);

  const [showExtraSection, setShowExtraSection] = useState<boolean>(false);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [months, setMonths] = useState<string[]>([]);
  const [baseCPT, setBaseCPT] = useState<number>(0);
  const [baseCPM, setBaseCPM] = useState<number>(0);
  const [totalLoading, setTotalLoading] = useState<number>(0);
  const [fiblibLoading, setFiblibLoading] = useState<number>(0);
  const [grossCPT, setGrossCPT] = useState<number>(0);
  const [grossCPM, setGrossCPM] = useState<number>(0);

  const toggleExtraSection = () => {
    setShowExtraSection(!showExtraSection);
  };

  const formatCurrency = (amount: number): string => {
    const currencyRounded = Math.round(amount);
    const formattedResult = currencyRounded
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return '$' + formattedResult;
  };

  useEffect(() => {
    if (selected) {
      const lowercaseSelection = mapPascalMonthToLowercaseMonth(
        selected as MONTH_PASCAL
      );
      const monthData = calculated[lowercaseSelection];

      setBaseCPT(monthData.baseCPT);
      setBaseCPM(monthData.baseCPM);
      setTotalLoading(monthData.totalLoading);
      setFiblibLoading(monthData.fiblibLoading);
      setGrossCPT(monthData.totalCPT);
      setGrossCPM(monthData.totalCPM);
    }
  }, [selected, calculated]);

  useEffect(() => {
    setSelected(detail.selectedMonth);
  }, [detail.selectedMonth]);

  useEffect(() => {
    const monthsInRange = getMonthsInRange(
      new Date(startDate),
      new Date(endDate)
    );
    setMonths(monthsInRange);
    setSelected(monthsInRange[0]);
  }, [startDate, endDate]);

  return (
    <ContentContainer>
      <StyledHeadingContainer>
        <CalculateOutlined />
        <div>
          <h2>Campaign Calculator</h2>
          <p>
            {format(parseISO(startDate), 'dd/MM/yyyy')} -{' '}
            {format(parseISO(endDate), 'dd/MM/yyyy')}
          </p>
        </div>
      </StyledHeadingContainer>
      <SelectField
        id="displayValues"
        label="Display values for"
        value={selected}
        onChange={(value) => setSelected(value)}
        options={Object.values(months)}
      />
      <Divider />
      <StyledHeading>Base Values</StyledHeading>
      <StyledBluePill>
        <StyledPillLabel>Advertiser Discount</StyledPillLabel>
        <StyledPillValue>
          {(detail.advertiserDiscount * 100).toFixed(2)}%
        </StyledPillValue>
      </StyledBluePill>
      <StyledTwoColumn>
        <StyledGreyPill>
          <StyledPillLabel>Base CPT</StyledPillLabel>
          <StyledPillValue>{formatCurrency(baseCPT)}</StyledPillValue>
        </StyledGreyPill>
        <StyledGreyPill>
          <StyledPillLabel>Base CPM</StyledPillLabel>
          <StyledPillValue>{formatCurrency(baseCPM)}</StyledPillValue>
        </StyledGreyPill>
      </StyledTwoColumn>
      {showExtraSection && (
        <StyledExtraSection>
          <StyledHeading>Loading Values</StyledHeading>
          <StyledBluePill>
            <StyledPillLabel>Total Loading</StyledPillLabel>
            <StyledPillValue>
              {roundTwoDecimals(totalLoading).toFixed(2)}%
            </StyledPillValue>
          </StyledBluePill>
          <StyledGreyPill style={{ width: 'auto' }}>
            <StyledPillLabel>FIB / LIB Loading</StyledPillLabel>
            <StyledPillValue>
              {roundTwoDecimals(fiblibLoading).toFixed(2)}%
            </StyledPillValue>
          </StyledGreyPill>
          <StyledTwoColumn>
            <StyledGreyPill>
              <StyledPillLabel>Gross CPT</StyledPillLabel>
              <StyledPillValue>{formatCurrency(grossCPT)}</StyledPillValue>
            </StyledGreyPill>
            <StyledGreyPill>
              <StyledPillLabel>Gross CPM</StyledPillLabel>
              <StyledPillValue>{formatCurrency(grossCPM)}</StyledPillValue>
            </StyledGreyPill>
          </StyledTwoColumn>
        </StyledExtraSection>
      )}
      <StyledRightColumn>
        <Button
          icon={showExtraSection ? <Remove /> : <Add />}
          label={showExtraSection ? 'Hide Loading' : 'Show Loading'}
          backgroundColor="transparent"
          hoverBackgroundColor={COLORS.BUTTON_TERTIARY_HOVER}
          textColor={COLORS.PRIMARY_500}
          showShadow={false}
          borderColor={COLORS.NEUTRAL_700}
          onClick={toggleExtraSection}
        />
      </StyledRightColumn>
    </ContentContainer>
  );
};

export default CampaignCalculator;
